import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { getOfflineCurAccounts, uploadOfflineCurAccountFile } from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import UploadOfflineCurModal from "./components/UploadOfflineCurModal";

/** API sends UTC without a `Z` suffix; parse as UTC so toLocaleString can show browser-local time. */
const parseUtcTimestamp = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const hasExplicitTz =
    /[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s);
  const d = new Date(hasExplicitTz ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatTimestamp = (isoString) => {
  const d = parseUtcTimestamp(isoString);
  if (!d) return "—";
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch (error) {
    return String(isoString);
  }
};

/** Same pattern as Dashboard `WAFRInsight`: paper surface, border, shadow, arrow ring. */
const offlineCurDashboardTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: 3,
      maxWidth: "min(90vw, 22rem)",
      p: 0,
      "& .MuiTooltip-arrow": {
        color: "background.paper",
        "&::before": {
          border: "1px solid",
          borderColor: "divider",
        },
      },
    },
  },
  /** Popper flips / shifts by default; do not force placement — MUI default is `bottom`. */
  popper: {
    modifiers: [
      { name: "flip", enabled: true },
      { name: "preventOverflow", enabled: true, options: { padding: 8 } },
    ],
  },
};

const formatPeriodLabel = (periodRaw) => {
  const text = String(periodRaw ?? "").trim();
  const m = text.match(/^(\d{4})-(\d{2})$/);
  if (!m) return text || "—";
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return text;
  const d = new Date(Date.UTC(year, monthIndex, 1));
  try {
    return d.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  } catch (error) {
    return text;
  }
};

const normalizeOfflineCurRow = (raw, index) => {
  const accountNumber =
    raw.accountNumber != null && String(raw.accountNumber).trim() !== ""
      ? String(raw.accountNumber).trim()
      : raw.account_number != null && String(raw.account_number).trim() !== ""
        ? String(raw.account_number).trim()
        : raw.account_id != null
          ? String(raw.account_id)
          : raw.accountId != null
            ? String(raw.accountId)
            : "—";

  const periodRaw =
    raw.period != null && String(raw.period).trim() !== ""
      ? String(raw.period).trim()
      : raw.year != null && raw.month != null
        ? `${raw.year}-${String(raw.month).padStart(2, "0")}`
        : "—";

  const uploadedAt =
    raw.uploadedDate ??
    raw.uploaded_date ??
    raw.uploaded_at ??
    raw.uploadedAt ??
    raw.created_at ??
    raw.createdAt ??
    null;

  const id =
    raw.id ??
    raw.uuid ??
    `${accountNumber}-${uploadedAt ?? index}-${raw.fileName ?? index}`;

  return {
    id: String(id),
    accountNumber,
    username:
      raw.username != null && String(raw.username).trim() !== ""
        ? String(raw.username).trim()
        : raw.user_name ?? raw.userName ?? "—",
    period: periodRaw,
    fileName:
      raw.fileName ??
      raw.file_name ??
      raw.filename ??
      raw.original_filename ??
      raw.originalFilename ??
      "—",
    processType:
      raw.processType ??
      raw.process_type ??
      raw.type ??
      "—",
    status:
      raw.status ??
      raw.upload_status ??
      raw.uploadStatus ??
      raw.state ??
      "—",
    uploadedAt,
    processedAt:
      raw.processedDate ??
      raw.processed_date ??
      raw.processed_at ??
      raw.processedAt ??
      null,
    errorText:
      raw.errorText != null && String(raw.errorText).trim() !== ""
        ? String(raw.errorText).trim()
        : raw.error_text != null && String(raw.error_text).trim() !== ""
          ? String(raw.error_text).trim()
          : raw.errorMessage != null && String(raw.errorMessage).trim() !== ""
            ? String(raw.errorMessage).trim()
            : null,
  };
};

/** Normalize API `accountNumbers` for dropdowns (sorted, unique). */
const normalizeAccountNumbersList = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const list = [];
  raw.forEach((x) => {
    const s = x != null ? String(x).trim() : "";
    if (s && !seen.has(s)) {
      seen.add(s);
      list.push(s);
    }
  });
  return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

const extractOfflineCurItems = (resp) => {
  if (!resp || typeof resp !== "object") return [];
  if (resp.status != null && resp.status >= 400) return [];

  const fromObject = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    if (Array.isArray(obj)) return obj;
    for (const key of [
      "auditList",
      "items",
      "accounts",
      "results",
      "content",
      "records",
      "rows",
    ]) {
      if (Array.isArray(obj[key])) return obj[key];
    }
    if (Array.isArray(obj.data)) return obj.data;
    if (obj.data && typeof obj.data === "object" && Array.isArray(obj.data.items)) {
      return obj.data.items;
    }
    return null;
  };

  const direct = fromObject(resp);
  if (direct) return direct;

  const numericKeys = Object.keys(resp).filter((k) => k !== "status" && k !== "isSynthetic" && /^\d+$/.test(k));
  if (numericKeys.length > 0) {
    return numericKeys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => resp[k])
      .filter((row) => row != null && typeof row === "object");
  }

  return [];
};

const formatStatusDisplay = (statusRaw) => {
  const normalized = String(statusRaw ?? "").trim().toUpperCase();
  if (normalized === "UPLOADED") {
    return { label: "Queued", severity: "default", reason: "" };
  }
  if (normalized === "REJECTED") {
    return {
      label: "Failed",
      severity: "error",
      reason: "Rejected due to incorrect file",
    };
  }
  return {
    label: String(statusRaw ?? "—").trim() || "—",
    severity: "default",
    reason: "",
  };
};

const OfflineCur = () => {
  const [accountFilter, setAccountFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const cancelUploadRef = useRef(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [rows, setRows] = useState([]);
  /** From API `accountNumbers` — used for filter dropdown and upload modal. */
  const [accountNumbersList, setAccountNumbersList] = useState([]);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const loadAccounts = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const response = await getOfflineCurAccounts();
      if (response?.status >= 200 && response?.status < 300) {
        const items = extractOfflineCurItems(response);
        setRows(items.map((item, i) => normalizeOfflineCurRow(item, i)));
        setAccountNumbersList(normalizeAccountNumbersList(response.accountNumbers));
        return;
      }
      setRows([]);
      setAccountNumbersList([]);
      setListError(getApiErrorMessage(response));
    } catch (error) {
      setRows([]);
      setAccountNumbersList([]);
      setListError(getApiErrorMessage(error?.response?.data ?? error?.data ?? error));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (accountFilter && !accountNumbersList.includes(accountFilter)) {
      setAccountFilter("");
    }
  }, [accountNumbersList, accountFilter]);

  const periodOptions = useMemo(() => {
    const uniquePeriods = new Set();
    rows.forEach((row) => {
      const p = String(row.period ?? "").trim();
      if (p && p !== "—") uniquePeriods.add(p);
    });
    return Array.from(uniquePeriods).sort((a, b) => b.localeCompare(a));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (accountFilter) {
      next = next.filter((row) => String(row.accountNumber).trim() === accountFilter);
    }
    if (periodFilter) {
      next = next.filter((row) => String(row.period).trim() === periodFilter);
    }
    if (!searchTerm.trim()) return next;
    const normalizedSearch = searchTerm.toLowerCase();
    return next.filter((row) => {
      const statusText = formatStatusDisplay(row.status).label.toLowerCase();
      return (
        String(row.accountNumber).toLowerCase().includes(normalizedSearch) ||
        String(row.username).toLowerCase().includes(normalizedSearch) ||
        String(row.period).toLowerCase().includes(normalizedSearch) ||
        String(row.fileName).toLowerCase().includes(normalizedSearch) ||
        String(row.processType).toLowerCase().includes(normalizedSearch) ||
        String(row.errorText ?? "").toLowerCase().includes(normalizedSearch) ||
        statusText.includes(normalizedSearch)
      );
    });
  }, [rows, accountFilter, periodFilter, searchTerm]);

  useEffect(() => {
    if (periodFilter && !periodOptions.includes(periodFilter)) {
      setPeriodFilter("");
    }
  }, [periodFilter, periodOptions]);

  const groupedRows = useMemo(() => {
    const groupedByAccount = new Map();
    filteredRows.forEach((row) => {
      const accountKey = String(row.accountNumber || "—");
      if (!groupedByAccount.has(accountKey)) {
        groupedByAccount.set(accountKey, new Map());
      }
      const periodKey = String(row.period || "—");
      const periodMap = groupedByAccount.get(accountKey);
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, []);
      }
      periodMap.get(periodKey).push(row);
    });

    return Array.from(groupedByAccount.entries())
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .map(([accountNumber, periodMap]) => {
        const periods = Array.from(periodMap.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([period, items]) => ({
            period,
            key: `${accountNumber}__${period}`,
            items: [...items].sort((x, y) => {
              const xT = parseUtcTimestamp(x.uploadedAt)?.getTime() ?? 0;
              const yT = parseUtcTimestamp(y.uploadedAt)?.getTime() ?? 0;
              return yT - xT;
            }),
          }));
        return {
          accountNumber,
          key: `acct__${accountNumber}`,
          periods,
          totalItems: periods.reduce((sum, p) => sum + p.items.length, 0),
        };
      });
  }, [filteredRows]);

  const totalGroups = groupedRows.length;
  const maxPage = Math.max(0, Math.ceil(totalGroups / rowsPerPage) - 1);
  const pagedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return groupedRows.slice(start, start + rowsPerPage);
  }, [groupedRows, page, rowsPerPage]);

  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage]);

  useEffect(() => {
    setPage(0);
  }, [accountFilter, periodFilter, searchTerm, rowsPerPage]);

  useEffect(() => {
    setExpandedAccounts((prev) => {
      const next = { ...prev };
      pagedGroups.forEach((group) => {
        if (next[group.key] == null) next[group.key] = true;
      });
      return next;
    });
  }, [pagedGroups]);

  useEffect(() => {
    setExpandedPeriods((prev) => {
      const next = { ...prev };
      pagedGroups.forEach((group) => {
        group.periods.forEach((periodGroup) => {
          if (next[periodGroup.key] == null) next[periodGroup.key] = true;
        });
      });
      return next;
    });
  }, [pagedGroups]);

  const handleUpload = async ({ accountNumber, year, month, file, files }) => {
    const fileList =
      Array.isArray(files) && files.length > 0 ? files : file ? [file] : [];
    if (fileList.length === 0) return false;

    cancelUploadRef.current = false;
    setIsUploading(true);
    setUploadProgress(null);

    try {
      for (let i = 0; i < fileList.length; i += 1) {
        if (cancelUploadRef.current) {
          setSnackbarState({
            open: true,
            message: "Upload cancelled.",
            severity: "info",
          });
          return false;
        }

        setUploadProgress({
          fileIndex: i + 1,
          fileCount: fileList.length,
          currentFileName: fileList[i].name,
        });

        const response = await uploadOfflineCurAccountFile({
          accountNumber,
          year,
          month,
          file: fileList[i],
          onProgress: (p) =>
            setUploadProgress({
              ...p,
              fileIndex: i + 1,
              fileCount: fileList.length,
              currentFileName: fileList[i].name,
            }),
          shouldAbort: () => cancelUploadRef.current,
        });

        if (response?.status === 499) {
          setSnackbarState({
            open: true,
            message: response?.data?.message || "Upload cancelled.",
            severity: "info",
          });
          return false;
        }

        if (!(response?.status >= 200 && response?.status < 300)) {
          setSnackbarState({
            open: true,
            message:
              fileList.length > 1
                ? `${getApiErrorMessage(response?.data ?? response)} (file ${i + 1} of ${
                    fileList.length
                  }: ${fileList[i].name})`
                : getApiErrorMessage(response?.data ?? response),
            severity: "error",
          });
          return false;
        }
      }

      await loadAccounts();
      setSnackbarState({
        open: true,
        message:
          fileList.length > 1
            ? `${fileList.length} file(s) uploaded successfully.`
            : "Offline CUR file uploaded successfully.",
        severity: "success",
      });
      return true;
    } catch (error) {
      setSnackbarState({
        open: true,
        message: getApiErrorMessage(error?.response?.data ?? error?.data ?? error),
        severity: "error",
      });
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const requestCancelUpload = useCallback(() => {
    cancelUploadRef.current = true;
  }, []);

  const toggleAccountGroup = (accountKey) => {
    setExpandedAccounts((prev) => ({ ...prev, [accountKey]: !prev[accountKey] }));
  };

  const togglePeriodGroup = (periodKey) => {
    setExpandedPeriods((prev) => ({ ...prev, [periodKey]: !prev[periodKey] }));
  };

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: "#fff",
        minHeight: "100%",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#0f172a",
            fontSize: "1.25rem",
            letterSpacing: "-0.01em",
            mb: 0.25,
          }}
        >
          Offline CUR
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            fontSize: "0.8125rem",
            fontWeight: 400,
          }}
        >
          Upload and track offline account CUR files.
        </Typography>
      </Box>

      {listError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
          {listError}
        </Alert>
      ) : null}

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#fff",
          fontFamily: '"Inter", system-ui, sans-serif',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid",
              borderColor: "rgba(0,0,0,0.06)",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
              <TextField
                size="small"
                placeholder="Search period, file, user, status…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#71717a" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 220,
                  maxWidth: 420,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    backgroundColor: "#fafafa",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: "6px",
                    height: "36px",
                    "&:hover": { borderColor: "rgba(0,0,0,0.12)", backgroundColor: "#fff" },
                    "&.Mui-focused": {
                      borderColor: "#18181b",
                      backgroundColor: "#fff",
                      boxShadow: "0 0 0 2px rgba(24,24,27,0.1)",
                    },
                    "& fieldset": { border: "none" },
                  },
                }}
              />
              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: 220 },
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    backgroundColor: "#fafafa",
                    borderRadius: "6px",
                    "&:hover": { backgroundColor: "#fff" },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      boxShadow: "0 0 0 2px rgba(24,24,27,0.1)",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0,0,0,0.08)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0,0,0,0.12)",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#18181b",
                    borderWidth: "1px",
                  },
                }}
              >
                <InputLabel id="offline-cur-account-filter-label">Account number</InputLabel>
                <Select
                  labelId="offline-cur-account-filter-label"
                  label="Account number"
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">
                    <em>All accounts</em>
                  </MenuItem>
                  {accountNumbersList.map((acc) => (
                    <MenuItem key={acc} value={acc}>
                      {acc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: 190 },
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    backgroundColor: "#fafafa",
                    borderRadius: "6px",
                  },
                }}
              >
                <InputLabel id="offline-cur-period-filter-label">Month/year</InputLabel>
                <Select
                  labelId="offline-cur-period-filter-label"
                  label="Month/year"
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">
                    <em>All periods</em>
                  </MenuItem>
                  {periodOptions.map((period) => (
                    <MenuItem key={period} value={period}>
                      {formatPeriodLabel(period)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon fontSize="small" />}
              onClick={() => setIsUploadModalOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                fontWeight: 600,
                boxShadow: "none",
              }}
            >
              Upload
            </Button>
          </Box>

          <Alert
            severity="info"
            sx={{
              mt: 2,
              mb: 0,
              fontSize: "0.8125rem",
              borderRadius: "8px",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            CUR file processing happens everyday at 09:00 EST.
          </Alert>

          <TableContainer sx={{ maxHeight: "calc(100vh - 360px)" }}>
            <Table stickyHeader size="medium" sx={{ minWidth: 1080, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      backgroundColor: "#f4f4f5",
                      borderBottom: "2px solid #e4e4e7",
                      py: 1.5,
                      px: 2,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      color: "#52525b",
                    },
                  }}
                >
                  <TableCell sx={{ width: "4%" }} />
                  <TableCell sx={{ width: "14%" }}>Account / Period</TableCell>
                  <TableCell sx={{ width: "24%" }}>File name</TableCell>
                  <TableCell sx={{ width: "12%" }}>Type</TableCell>
                  <TableCell sx={{ width: "12%" }}>Status</TableCell>
                  <TableCell sx={{ width: "14%" }}>Processed</TableCell>
                  <TableCell sx={{ width: "20%" }}>Uploaded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} sx={{ color: "#52525b" }} />
                    </TableCell>
                  </TableRow>
                ) : groupedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#71717a" }}>
                      No offline CUR uploads found.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedGroups.map((accountGroup) => {
                    const accountOpen = expandedAccounts[accountGroup.key] !== false;
                    return (
                      <React.Fragment key={accountGroup.key}>
                        <TableRow sx={{ backgroundColor: "#fafafa" }}>
                          <TableCell sx={{ py: 1, px: 1 }}>
                            <IconButton size="small" onClick={() => toggleAccountGroup(accountGroup.key)}>
                              {accountOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell colSpan={6} sx={{ py: 1.25, fontWeight: 600, color: "#111827" }}>
                            {accountGroup.accountNumber}
                            <Typography component="span" sx={{ ml: 1, color: "#6b7280", fontSize: "0.8rem" }}>
                              ({accountGroup.totalItems} file{accountGroup.totalItems === 1 ? "" : "s"})
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {accountOpen &&
                          accountGroup.periods.map((periodGroup) => {
                            const periodOpen = expandedPeriods[periodGroup.key] !== false;
                            return (
                              <React.Fragment key={periodGroup.key}>
                                <TableRow sx={{ backgroundColor: "#fcfcfd" }}>
                                  <TableCell sx={{ py: 1, px: 1, pl: 4 }}>
                                    <IconButton size="small" onClick={() => togglePeriodGroup(periodGroup.key)}>
                                      {periodOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell colSpan={6} sx={{ py: 1, fontWeight: 500, color: "#374151" }}>
                                    {formatPeriodLabel(periodGroup.period)}
                                    <Typography component="span" sx={{ ml: 1, color: "#6b7280", fontSize: "0.8rem" }}>
                                      ({periodGroup.items.length} upload{periodGroup.items.length === 1 ? "" : "s"})
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                {periodOpen &&
                                  periodGroup.items.map((row) => {
                                    const statusMeta = formatStatusDisplay(row.status);
                                    const hasApiErrorText =
                                      row.errorText != null && String(row.errorText).trim() !== "";
                                    const showStatusHelp = hasApiErrorText || Boolean(statusMeta.reason);
                                    const statusChipOnly = (
                                      <Chip
                                        label={statusMeta.label}
                                        size="small"
                                        color={statusMeta.severity === "error" ? "error" : "default"}
                                        variant={statusMeta.severity === "error" ? "filled" : "outlined"}
                                      />
                                    );
                                    const statusWithHint = (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {statusChipOnly}
                                        <ErrorOutlineIcon
                                          sx={{
                                            fontSize: 18,
                                            color: "error.main",
                                            flexShrink: 0,
                                          }}
                                          aria-hidden
                                        />
                                      </Box>
                                    );
                                    return (
                                      <TableRow
                                        key={row.id}
                                        sx={{
                                          "& td": {
                                            borderBottom: "1px solid #e4e4e7",
                                            py: 1.25,
                                            px: 2,
                                            fontSize: "0.875rem",
                                          },
                                          "&:hover": { bgcolor: "#fafafa" },
                                        }}
                                      >
                                        <TableCell sx={{ pl: 6 }} />
                                        <TableCell sx={{ color: "#6b7280" }}>{formatPeriodLabel(row.period)}</TableCell>
                                        <TableCell sx={{ wordBreak: "break-all" }}>{row.fileName}</TableCell>
                                        <TableCell>{row.processType}</TableCell>
                                        <TableCell>
                                          {showStatusHelp ? (
                                            <Tooltip
                                              arrow
                                              interactive={hasApiErrorText}
                                              slotProps={
                                                hasApiErrorText
                                                  ? {
                                                      ...offlineCurDashboardTooltipSlotProps,
                                                      tooltip: {
                                                        ...offlineCurDashboardTooltipSlotProps.tooltip,
                                                        sx: {
                                                          ...offlineCurDashboardTooltipSlotProps.tooltip.sx,
                                                          maxWidth: "min(95vw, 52rem)",
                                                        },
                                                      },
                                                    }
                                                  : offlineCurDashboardTooltipSlotProps
                                              }
                                              title={
                                                hasApiErrorText ? (
                                                  <Box sx={{ p: 1, minWidth: 200, maxWidth: "min(95vw, 52rem)" }}>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                                                    >
                                                      Error details
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        color: "text.secondary",
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        lineHeight: 1.5,
                                                      }}
                                                    >
                                                      {String(row.errorText ?? "").trim()}
                                                    </Typography>
                                                  </Box>
                                                ) : (
                                                  <Box sx={{ p: 1, maxWidth: 320 }}>
                                                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                                                      {statusMeta.reason}
                                                    </Typography>
                                                  </Box>
                                                )
                                              }
                                            >
                                              {statusWithHint}
                                            </Tooltip>
                                          ) : (
                                            statusChipOnly
                                          )}
                                        </TableCell>
                                        <TableCell>{formatTimestamp(row.processedAt)}</TableCell>
                                        <TableCell>
                                          <Typography sx={{ fontSize: "0.84rem", wordBreak: "break-word" }}>{row.username}</Typography>
                                          <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>
                                            {formatTimestamp(row.uploadedAt)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!listLoading && groupedRows.length > 0 ? (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: "1px solid #e4e4e7",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: "0.82rem", color: "#6b7280" }}>
                Showing {Math.min(totalGroups, page * rowsPerPage + 1)}-
                {Math.min(totalGroups, (page + 1) * rowsPerPage)} of {totalGroups} account groups
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "0.82rem", color: "#6b7280" }}>Groups per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  sx={{ minWidth: 84, fontSize: "0.84rem", height: 34 }}
                >
                  {[3, 5, 10, 20].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{ minWidth: 74, textTransform: "none" }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= maxPage}
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                  sx={{ minWidth: 54, textTransform: "none" }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <UploadOfflineCurModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={handleUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        onRequestCancelUpload={requestCancelUpload}
        accountNumbers={accountNumbersList}
      />

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbarState.severity}
          onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OfflineCur;
